import { MoreIcon } from 'components/icons'
import { SelectProvider } from 'context/select-context'
import classNames from 'helpers/class-names'
import { FC, MouseEvent, SVGProps, useMemo, useRef, useState } from 'react'
import {
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  Placement,
  autoUpdate,
  flip,
  offset as floatingOffset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from '@floating-ui/react'

type MenuProps = {
  className?: string
  disabled?: boolean
  buttonClass?: string
  iconClass?: string | ((open: boolean) => string)
  icon?: FC<SVGProps<SVGSVGElement>>
  trigger?: FC<{ open: boolean }>
  onClick?: (event: MouseEvent) => void
  offset?: [number, number]
  placement?: Placement
  title?: string
}

const Menu: FC<MenuProps> = ({
  className,
  children,
  disabled,
  buttonClass,
  iconClass,
  icon: IconComponent = MoreIcon,
  trigger: Trigger,
  onClick,
  offset = null,
  placement: menuPlacement = 'bottom-end',
  title = 'More Options',
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const { refs, floatingStyles, context, placement } = useFloating({
    open: open,
    onOpenChange: setOpen,
    placement: menuPlacement,
    whileElementsMounted: autoUpdate,
    middleware: [
      floatingOffset({
        mainAxis: offset ? offset[0] : 6,
        crossAxis: offset ? offset[1] : 5,
      }),
      flip({ crossAxis: false }),
      shift(),
    ],
  })

  const elementsRef = useRef<Array<HTMLButtonElement | null>>([])
  const labelsRef = useRef<Array<string | null>>([])

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'menu' })
  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    onNavigate: setActiveIndex,
    activeIndex,
  })
  const typeahead = useTypeahead(context, {
    enabled: open,
    listRef: labelsRef,
    onMatch: setActiveIndex,
    activeIndex,
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [role, dismiss, listNavigation, typeahead, click],
  )

  const selectContext = useMemo(
    () => ({
      activeIndex,
      getItemProps,
      handleSelect: () => {
        setOpen(false)
      },
    }),
    [activeIndex, getItemProps],
  )

  const classes = classNames(
    'w-56 transition duration-300 ease-in-out font-body',
    {
      block: open,
      hidden: !open,
      'origin-top-right': placement === 'bottom-end',
      'origin-bottom-right':
        placement === 'top-end' || placement === 'left-start',
      'origin-bottom-left': placement === 'right-start',
    },
  )

  return (
    <div className={classNames('relative', className)}>
      <button
        type="button"
        data-testid="menu-trigger"
        ref={refs.setReference}
        title={title}
        aria-label={title}
        disabled={disabled}
        className={classNames(
          'h-9 w-7 rounded-lg text-gray-200 transition duration-200 ease-in-out',
          'hover:text-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-info/75',
          { 'hover:cursor-not-allowed': disabled },
          buttonClass,
        )}
        {...getReferenceProps()}
        onClick={(event: MouseEvent) => {
          event.stopPropagation()
          // @ts-ignore
          getReferenceProps().onClick(event)
          if (typeof onClick === 'function') {
            onClick(event)
          }
        }}>
        {Trigger ? (
          <Trigger open={open} />
        ) : (
          <IconComponent
            className={classNames(
              'h-7 w-7',
              {
                'text-info': open,
              },
              typeof iconClass === 'function' ? iconClass(open) : iconClass,
            )}
          />
        )}
      </button>
      <SelectProvider value={selectContext}>
        <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
          <FloatingPortal>
            <FloatingFocusManager
              context={context}
              initialFocus={refs.floating}>
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                className={classNames('w-56 outline-none', {
                  'pointer-events-none': !open,
                })}
                {...getFloatingProps()}>
                <div className={classes}>
                  <div className="divide-gray-100 rounded-md border border-gray-200 bg-white text-base shadow-lg focus:outline-none dark:border-gray-800 dark:bg-gray-500 sm:text-sm">
                    <div
                      className={classNames(
                        'absolute right-3 z-0 mr-px h-3 w-3 rotate-45',
                        ' border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-500',
                        {
                          '-top-3 mt-1.5 border-l border-t':
                            placement === 'bottom-end',
                        },
                        {
                          '-bottom-1.5 border-b border-r':
                            placement === 'top-end',
                        },
                        {
                          '-left-1.5 bottom-3 -rotate-45 -scale-100 border-b border-r':
                            placement === 'right-start',
                        },
                      )}
                    />
                    <ul>{children}</ul>
                  </div>
                </div>
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        </FloatingList>
      </SelectProvider>
    </div>
  )
}

export default Menu
